import React from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import {
  Route, Switch, Redirect, withRouter, useLocation,
} from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import cx from 'classnames';
import configs from 'configs';
import { Popups } from 'components/Popups/Popups';
import { Alert } from 'components/Alert';
import { GA_SIGNUP_CATEGORY, GA_SIGNUP } from 'modules/ga/generalEvents';
import { PENDING, SUCCESS } from 'utils/constants/queryStatuses';
import { PROVIDER_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import Splash from 'components/Splash';
import { SimpleBackdrop } from 'components/SimpleBackdrop/SimpleBackdrop';
import { BACKDROP_TYPES } from 'modules/backdrop/constants';
import { saveProviderInfo } from 'modules/user/api';
import { useProviderSpecificConfiguration } from 'modules/appConfiguration/useProviderSpecificConfiguration';
import mainConstants from 'utils/constants/main';
import { useIsMobileResolution } from 'utils/hooks/useIsMobileResolution';
import { ROUTE_NAME, ROUTE_PATH } from 'utils/constants/router';
import analyticsService from 'functions/analytics';
import { FB_EVENTS, FB_TRACK_TYPE } from 'functions/analytics/constants/fbKeys';
import { Home } from 'pages/Home';
import { Signup } from 'components/Authentication/components/Signup';
import { AfterSignupUserDetails } from 'components/Authentication/components/AfterSignupUserDetails';
import { QuestionnairePage } from 'pages/QuestionnairePage';
import { Login } from 'pages/Login/Login';
import { ForgotPassword } from 'pages/Login/ForgotPassword';
import { ResetPassword } from 'pages/Login/ResetPassword';
import Maintenance from 'pages/Maintenance';
import ChatPage from 'pages/Chat';
import { Articles } from 'pages/Articles';
import { Patients } from 'pages/Patients';
import { FinanceSupport } from 'pages/FinanceSupport';
import { Preloader } from 'components/Preloader';
import { PageHeader } from 'components/PageHeader/PageHeader';
import Footer from 'components/Footer';
import app from 'new-ui/app';
import {
  getBackdropTypeToShow, getIsDashboardRoute, getIsFooterShown, getIsPageLayoutRoute,
} from './functions';
import { ExternalLogoutListener } from './ExternalLogoutListener';
import { AppLogic } from './AppLogic';
import { Dashboard } from './components/Dashboard';
import PrivateRoute from './components/PrivateRoute';
import { PrivateRouteV2 } from './components/PrivateRouteV2';
import { PublicRoute } from './components/PublicRoute';
import ProtectedRoute from './components/ProtectedRoute';
import { ScrollManager } from './ScrollManager';

// DEV-1001, DEV-1323
// ?provider=mpnrf&utm_source=facebook&refid=fb123&utm_term=BULLDOZER
// corresponds to the following columns in userFlowStatistic model (ps)
// providerId: 'provider'
// utm_source: 'utm_source'
// ref_id: 'refid'
// utm_term: 'utm_term'
// ctm: 'ctm'
const USER_FLOW_STATISTIC_URL_SEARCH_PARAMS = [
  PROVIDER_URL_SEARCH_PARAM,
  'utm_source',
  'refid',
  'utm_term',
  // ctm
  // 'redirectTo'
];

const PreloaderBackdrop = () => <Preloader isVisible />;

const renderBackdrop = (type) => {
  const Component = {
    [BACKDROP_TYPES.PRELOADER]: PreloaderBackdrop,
    [BACKDROP_TYPES.SPLASH]: Splash,
    [BACKDROP_TYPES.SIMPLE_BACKDROP]: SimpleBackdrop,
  }[type];
  return Component ? <Component /> : null;
};

const getMainDashboardClassNames = () => cx(
  'app',
  'new-ui',
  `main-${window.location.pathname.slice(1)}`,
);

const FooterWrapper = ({ isDashboard }) => {
  const isMobile = useIsMobileResolution();
  const { pathname } = useLocation();
  const isQuestionnaireInitialized = useSelector((state) => state.questionnaire.isInitialized);
  const { code: provider } = useProviderSpecificConfiguration();
  const isFooterShown = getIsFooterShown({
    isDashboard, provider, pathname, isMobile, isQuestionnaireInitialized,
  });
  return isFooterShown ? <Footer /> : null;
};

FooterWrapper.propTypes = {
  isDashboard: PropTypes.bool.isRequired,
};

class AppRoutes extends React.Component {
  isProviderInfoSaved = false;

  componentDidUpdate(prevProps) {
    const {
      loggedIn,
      cameFrom,
      urlParameters,
      dispatch,
      lastOpenedUrl,
      appConfigurationQueryStatus,
    } = this.props;
    const { appConfigurationQueryStatus: prevAppConfigurationQueryStatus } = prevProps;

    const url = window?.location.href || '';
    if (lastOpenedUrl !== url) {
      dispatch({
        type: mainConstants.SET_LAST_OPENED_URL,
        payload: url,
      });

      const eventID = `${FB_EVENTS.PAGE_VIEW}.${Date.now()}`;
      if (
        typeof window.fbq === 'function'
        && !!configs.metaPixelId
      ) {
        window.fbq(
          FB_TRACK_TYPE.TRACK_SINGLE,
          configs.metaPixelId,
          FB_EVENTS.PAGE_VIEW,
          {},
          { eventID },
        );
      }
      analyticsService.sendFbEventToConversionApi({
        eventName: FB_EVENTS.PAGE_VIEW,
        eventID,
      }).catch((e) => console.error('Can\'t send facebook event', e));
    }

    if (!loggedIn && prevAppConfigurationQueryStatus === PENDING && appConfigurationQueryStatus === SUCCESS) {
      // wait for appConfiguration.providerSpecific to be resolved
      app.sendGoogleAnalyticsEvent(GA_SIGNUP_CATEGORY, GA_SIGNUP.VISITOR_LANDED);
    }

    if (
      loggedIn
      && !this.isProviderInfoSaved
      // shouldn't provider urlParameter be mandatory?
      && USER_FLOW_STATISTIC_URL_SEARCH_PARAMS.some((condition) => urlParameters?.includes(condition))
    ) {
      // optimistically
      this.isProviderInfoSaved = true;
      saveProviderInfo({
        cameFrom,
        urlParameters: urlParameters.split('?').pop(),
      }).catch((ex) => {
        this.isProviderInfoSaved = false;
        console.error('Can\'t save provider info', ex);
      });
    }
  }

  render() {
    const {
      location: { pathname },
      backdropTypeToShow,
    } = this.props;

    const isDashboard = getIsDashboardRoute(pathname);

    const isBackdropShown = !!backdropTypeToShow;

    return (
      <>
        <AppLogic />
        <ExternalLogoutListener />
        <ScrollManager />
        {isBackdropShown && renderBackdrop(backdropTypeToShow)}
        <PageHeader />
        <main
          id="router"
          inert={isBackdropShown ? '' : undefined}
          className={cx(
            { 'page-layout': isDashboard || getIsPageLayoutRoute(pathname) },
            isDashboard ? getMainDashboardClassNames() : null,
          )}
        >
          {isDashboard ? (
            <Dashboard />
          ) : (
            <Switch>
              <Route
                exact
                path={ROUTE_PATH[ROUTE_NAME.HOME]}
                component={Home}
              />
              <PublicRoute path={ROUTE_PATH[ROUTE_NAME.FORGOT_PASSWORD]} component={ForgotPassword} exact />
              <PublicRoute path={ROUTE_PATH[ROUTE_NAME.NEW_PASSWORD]} component={ResetPassword} exact />
              <PublicRoute path={ROUTE_PATH[ROUTE_NAME.LOGIN]} component={Login} exact />
              <ProtectedRoute path={ROUTE_PATH[ROUTE_NAME.QUESTIONNAIRE]} component={QuestionnairePage} />
              <ProtectedRoute
                isProfileRequired
                notRegistered
                path={ROUTE_PATH[ROUTE_NAME.SIGN_UP]}
                component={Signup}
              />
              <PrivateRoute
                path={ROUTE_PATH[ROUTE_NAME.AFTER_SIGNUP_USER_DETAILS]}
                component={AfterSignupUserDetails}
              />
              <PrivateRouteV2 path={ROUTE_PATH[ROUTE_NAME.ARTICLES]} component={Articles} />
              <PrivateRouteV2 path={ROUTE_PATH[ROUTE_NAME.CHAT]} component={ChatPage} />
              <PrivateRouteV2 path={ROUTE_PATH[ROUTE_NAME.PATIENTS]} component={Patients} />
              <Route
                path={ROUTE_PATH[ROUTE_NAME.PUBLIC_FINANCE]}
                render={(props) => <FinanceSupport isPublicFinance {...props} />}
                exact
              />
              <Route path={ROUTE_PATH[ROUTE_NAME.MAINTENANCE]} component={Maintenance} exact />
              <Redirect to={ROUTE_PATH[ROUTE_NAME.HOME]} />
            </Switch>
          )}
        </main>
        <FooterWrapper isDashboard={isDashboard} />
        <Popups />
        <Alert />
      </>
    );
  }
}

AppRoutes.propTypes = {
  location: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  backdropTypeToShow: PropTypes.oneOf(Object.values(BACKDROP_TYPES)),
  loggedIn: PropTypes.bool,
  cameFrom: PropTypes.string,
  urlParameters: PropTypes.string,
  appConfigurationQueryStatus: PropTypes.string.isRequired,
  lastOpenedUrl: PropTypes.string,
};

const StyledAppRoutes = withRouter(AppRoutes);

const mapStateToProps = (state) => ({
  backdropTypeToShow: getBackdropTypeToShow(pick(state, ['authentication', 'userGet', 'userUpdate', 'backdrop'])),
  loggedIn: state?.authentication?.loggedIn,
  cameFrom: state.main.cameFrom,
  urlParameters: state.main.urlParameters,
  appConfigurationQueryStatus: state?.appConfiguration.status,
  lastOpenedUrl: state?.main?.lastOpenedUrl,
});

export default connect(mapStateToProps)(StyledAppRoutes);
